import Button from '@/components/Buttons/Button';
import { salesUrl } from '@/environment';
import studioThumbnailSrc from '@/images/avatars/studio_avatar_thumbnail.jpg';
import studioVideoSrc from '@/images/avatars/studio_avatar_video.mp4';
import videoCardVideo from '@/images/avatars/video_card_preview.mp4';
import videoCardImage from '@/images/avatars/video_card_preview.png';
import uploadAvatarSrc from '@/images/uploadAvatar.svg';
import { useUploadAvatar } from '@/pages/avatar';
import { useCreateAvatarRedirect, useCreateExpressAvatarRedirect } from '@/pages/avatar/hooks';
import { useAnalytics } from '@/services/analytics';
import { useFlag } from '@/services/featureFlags';
import { MixPanelEvent } from '@/utils/types';
import styled from '@emotion/styled';
import { useLocation } from 'react-router-dom';
import SuggestionCard from './SuggestionCard';

const CardList = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 2rem;
    margin-bottom: 2.6rem;

    @media (min-width: 1900px) {
        display: flex;
        gap: 2rem;
    }
`;

const UploadAvatarImage = styled.div`
    height: 100%;
    aspect-ratio: 1 / 1;
    border-radius: 10px;
    background: ${({ theme }) => theme.colors.black[100]};
    display: flex;
    justify-content: center;
    align-items: center;
`;

const VideoWrapper = styled.div`
    height: 100%;
    aspect-ratio: 1 / 1;
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const ExpressVideo = styled.video`
    height: 14rem;
`;

const StudioVideo = styled.video`
    height: 18rem;
`;

const PrimaryButton = styled(Button)`
    width: fit-content;
    height: fit-content;
    font-size: 1.4rem;
    line-height: 2rem;
    padding: 0.7rem 0.8rem;
`;

const GreyButton = styled(PrimaryButton)`
    background-color: ${({ theme }) => theme.colors.black[100]};

    &.MuiButton-contained:hover {
        background-color: ${({ theme }) => theme.colors.black[200]} !important;
    }
`;

function SuggestionCardList() {
    const mixpanel = useAnalytics();
    const { onUploadClick } = useUploadAvatar({ from: 'avatars-page-discoverability-card' });
    const isDirectExpressFlowEnabled = useFlag('direct_express_flow_enabled', false);
    const { onClick: onCreateAvatar } = useCreateAvatarRedirect();
    const { onClick: onCreateExpressAvatar } = useCreateExpressAvatarRedirect();
    const { pathname } = useLocation();

    const handleTalkToSales = () => {
        mixpanel?.track('contact', { event: 'click', from: 'sales' });
        window.open(salesUrl, '_blank');
    };

    const handleCreateAvatar = () => {
        mixpanel?.track('avatar-create-modal', {
            event: MixPanelEvent.CLICK,
            from: 'avatars-page-discoverability-card',
        });

        if (isDirectExpressFlowEnabled) {
            onCreateExpressAvatar({ from: pathname });
        } else {
            onCreateAvatar({ from: pathname });
        }
    };

    const handleVideoMouseEnter = (e: any) => {
        e.target.play();
    };

    const handleVideoMouseLeave = (e: any) => {
        e.target.load();
        e.target.currentTime = 0;
    };

    const talkImage = (
        <UploadAvatarImage>
            <img src={uploadAvatarSrc} alt="Upload avatar" />;
        </UploadAvatarImage>
    );

    const expressImage = (
        <VideoWrapper>
            <ExpressVideo
                src={videoCardVideo}
                poster={videoCardImage}
                onMouseEnter={handleVideoMouseEnter}
                onMouseLeave={handleVideoMouseLeave}
                loop
                muted
            />
        </VideoWrapper>
    );

    const studioImage = (
        <VideoWrapper>
            <StudioVideo
                src={studioVideoSrc}
                poster={studioThumbnailSrc}
                onMouseEnter={handleVideoMouseEnter}
                onMouseLeave={handleVideoMouseLeave}
                loop
                muted
            />
        </VideoWrapper>
    );

    return (
        <CardList>
            <SuggestionCard
                title="Create with a photo"
                description="Upload a photo to create talking head videos."
                button={<PrimaryButton onClick={onUploadClick}>Upload photo</PrimaryButton>}
                image={talkImage}
            />
            <SuggestionCard
                title="Create with a video"
                description="Higher quality video Avatars."
                button={<PrimaryButton onClick={handleCreateAvatar}>Create Avatar</PrimaryButton>}
                image={expressImage}
            />
            <SuggestionCard
                title="Studio Avatar"
                description="Partner with us for a fully tailored, lifelike Avatar."
                button={<GreyButton onClick={handleTalkToSales}>Contact sales</GreyButton>}
                image={studioImage}
            />
        </CardList>
    );
}

export default SuggestionCardList;
